import { useAuth } from '@/auth/AuthContext';
import { usePermissions } from '@/layouts/PermissionsContext';
import {
  buildFinanceMenu,
  canAccessFinancePath,
  defaultFinancePath,
  hasAnyFinanceAccess,
} from '@/layouts/permissions';
import {
  ApartmentOutlined,
  AuditOutlined,
  BankOutlined,
  DollarOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FundOutlined,
  IdcardOutlined,
  LogoutOutlined,
  MenuOutlined,
  PayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SolutionOutlined,
  SwapOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Dropdown,
  Input,
  Layout,
  List,
  Menu,
  type MenuProps,
  Modal,
  Result,
  Space,
  Typography,
} from 'antd';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

const { Header, Sider, Content } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

const GROUP_ICONS: Record<string, ReactNode> = {
  mine: <WalletOutlined />,
  review: <SafetyCertificateOutlined />,
  cashier: <PayCircleOutlined />,
  finance: <DollarOutlined />,
  system: <TeamOutlined />,
};

const NAV_ICONS: Record<string, ReactNode> = {
  '/my/claims': <WalletOutlined />,
  '/my/claims/new': <PlusOutlined />,
  '/my/profile': <IdcardOutlined />,
  '/review': <SafetyCertificateOutlined />,
  '/cashier': <PayCircleOutlined />,
  '/overview': <DollarOutlined />,
  '/claims': <FileDoneOutlined />,
  '/transactions': <SwapOutlined />,
  '/obligations': <FileTextOutlined />,
  '/accounts': <BankOutlined />,
  '/categories': <TagsOutlined />,
  '/supplier-statements': <AuditOutlined />,
  '/reports': <FundOutlined />,
  '/employees': <SolutionOutlined />,
  '/departments': <ApartmentOutlined />,
  '/audit': <FileSearchOutlined />,
};

/**
 * 财务系统主框架：左侧导航（客户端按权限位生成）+ 顶栏 + 内容区。
 *
 * 复用 admin-web 那套 admin-shell 响应式类名，但信息架构完全是财务自己的：
 * 报销填报人只看得到「我的报销」，财务人员才看得到「财务管理」。
 */
export function FinanceLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { identity, grant, loading, failed, reload } = usePermissions();
  const [collapsed, setCollapsed] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const menu = useMemo(() => buildFinanceMenu(grant), [grant]);
  const landing = useMemo(() => defaultFinancePath(grant), [grant]);

  const navItems = useMemo<MenuItem[]>(
    () =>
      menu.map((group) => ({
        key: `group:${group.key}`,
        icon: GROUP_ICONS[group.key],
        label: group.label,
        children: group.items.map((item) => ({
          key: item.href,
          icon: NAV_ICONS[item.href],
          label: <Link to={item.href}>{item.label}</Link>,
        })),
      })),
    [menu],
  );

  const flatItems = useMemo(
    () =>
      menu.flatMap((group) => group.items.map((item) => ({ ...item, groupLabel: group.label }))),
    [menu],
  );

  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return flatItems;
    return flatItems.filter((item) =>
      `${item.label} ${item.groupLabel} ${item.href}`.toLowerCase().includes(query),
    );
  }, [flatItems, searchQuery]);

  // 选中态：取最长匹配前缀（'/my/claims/new' 优先于 '/my/claims'）。
  const selectedKey = useMemo(() => {
    const keys = flatItems.map((item) => item.href).sort((a, b) => b.length - a.length);
    return (
      keys.find((key) => location.pathname === key || location.pathname.startsWith(`${key}/`)) ??
      location.pathname
    );
  }, [flatItems, location.pathname]);

  const currentPage = useMemo(() => {
    const hit = flatItems.find((item) => item.href === selectedKey);
    if (hit) return { title: hit.label, group: hit.groupLabel };
    return { title: '财务系统', group: 'DRAMA FINANCE' };
  }, [flatItems, selectedKey]);

  const userMenu: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => {
        logout();
        navigate('/login', { replace: true });
      },
    },
  ];

  const pageAllowed = !loading && canAccessFinancePath(location.pathname, grant);
  const noAccess = !loading && !failed && !hasAnyFinanceAccess(grant);

  const signOut = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // 超管刚建完档：不用退出重登，重新拉一次 /ext/finance/me 就能进来。
  const recheck = async () => {
    setRechecking(true);
    try {
      await reload();
    } finally {
      setRechecking(false);
    }
  };

  // 未建档 / 已离职的后台账号根本进不来：整页阻断，连导航都不渲染。
  if (!loading && !failed && !identity.can_login) {
    return (
      <Layout className="admin-shell">
        <Content className="admin-content" style={{ display: 'grid', placeItems: 'center' }}>
          <Result
            status="403"
            title="财务系统未开通"
            subTitle="当前账号尚未加入财务系统，请联系超级管理员建档"
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  loading={rechecking}
                  onClick={() => void recheck()}
                >
                  重新检查
                </Button>
                <Button icon={<LogoutOutlined />} onClick={signOut}>
                  退出登录
                </Button>
              </Space>
            }
          />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="admin-shell">
      <Sider
        className="admin-sidebar"
        width={240}
        collapsedWidth={isMobile ? 0 : 72}
        breakpoint="lg"
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        onBreakpoint={setIsMobile}
      >
        <div className="admin-sidebar__brand">
          <div className="admin-sidebar__mark">D</div>
          <div className="admin-sidebar__brand-copy">
            <span className="admin-sidebar__name">DramaClaw</span>
            <span className="admin-sidebar__meta">FINANCE</span>
          </div>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={menu.map((group) => `group:${group.key}`)}
          items={navItems}
        />
      </Sider>
      <Layout className={`admin-main${collapsed && !isMobile ? ' admin-main--collapsed' : ''}`}>
        <Header className="admin-header">
          {isMobile && (
            <Button
              className="admin-mobile-menu"
              aria-label="打开导航"
              icon={<MenuOutlined />}
              onClick={() => setCollapsed(false)}
            />
          )}
          <div className="admin-header__context">
            <div className="admin-header__eyebrow">{currentPage.group}</div>
            <div className="admin-header__title">{currentPage.title}</div>
          </div>
          <div className="admin-header__actions">
            <Button
              className="admin-search-trigger"
              icon={<SearchOutlined />}
              onClick={() => setSearchOpen(true)}
            >
              搜索财务功能 <Typography.Text type="secondary">⌘ K</Typography.Text>
            </Button>
            <Dropdown menu={{ items: userMenu }} placement="bottomRight">
              <span className="admin-user-trigger">
                <span className="admin-user-trigger__avatar">
                  <UserOutlined />
                </span>
                <span className="admin-user-trigger__name">
                  {user?.name || user?.email || '财务用户'}
                </span>
              </span>
            </Dropdown>
          </div>
        </Header>
        <Content className="admin-content">
          {failed && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="权限加载失败"
              description="后台权限服务可能不可用，请稍后刷新页面重试。"
            />
          )}
          {loading ? (
            <Result status="info" title="正在确认财务权限" />
          ) : noAccess ? (
            <Result
              status="403"
              title="403"
              subTitle="当前账号没有财务系统权限"
              extra={
                <Button type="primary" onClick={() => window.location.reload()}>
                  重新检查
                </Button>
              }
            />
          ) : !pageAllowed ? (
            <Result
              status="403"
              title="403"
              subTitle="当前账号没有该财务功能的权限。如需使用，请联系财务负责人授权。"
              extra={
                <Button type="primary" onClick={() => navigate(landing ?? '/', { replace: true })}>
                  返回可用功能
                </Button>
              }
            />
          ) : failed ? null : (
            <Outlet />
          )}
        </Content>
      </Layout>
      {isMobile && !collapsed && (
        <button
          type="button"
          className="admin-sidebar-backdrop"
          aria-label="关闭导航"
          onClick={() => setCollapsed(true)}
        />
      )}
      <Modal
        open={searchOpen}
        className="admin-search-modal"
        title="搜索财务功能"
        footer={null}
        onCancel={() => {
          setSearchOpen(false);
          setSearchQuery('');
        }}
        afterOpenChange={(open) => {
          if (!open) setSearchQuery('');
        }}
      >
        <Input
          autoFocus
          allowClear
          prefix={<SearchOutlined />}
          placeholder="输入功能名称，例如：报销、流水、账户"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          onPressEnter={() => {
            const first = searchResults[0];
            if (!first) return;
            navigate(first.href);
            setSearchOpen(false);
          }}
        />
        <List
          style={{ marginTop: 12, maxHeight: 420, overflow: 'auto' }}
          locale={{ emptyText: '没有匹配且有权限访问的功能' }}
          dataSource={searchResults}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button
                  key="open"
                  type="link"
                  onClick={() => {
                    navigate(item.href);
                    setSearchOpen(false);
                  }}
                >
                  打开
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={NAV_ICONS[item.href]}
                title={item.label}
                description={item.groupLabel}
              />
            </List.Item>
          )}
        />
      </Modal>
    </Layout>
  );
}

export default FinanceLayout;
